import { Injectable } from "@angular/core";
import { PlatformPortalApiService } from "./platform-portal-api.service";
import { Observable, of, tap, switchMap, Subject, BehaviorSubject, debounceTime, catchError } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { UserSettings } from "../models/user-settings";

@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  private userSettings: UserSettings | null = null;
  private readonly settingsSubject = new BehaviorSubject<UserSettings | null>(null);
  private readonly saveSubject = new Subject<void>();

  // Public observable of settings
  readonly settings$ = this.settingsSubject.asObservable().pipe(
    map(settings => settings as UserSettings), // Filter out null
    shareReplay(1)
  );

  constructor(private readonly platformPortalApiService: PlatformPortalApiService) {
    // Set up the subscription for auto-saving
    this.setupAutoSave();
  }

  private setupAutoSave(): void {
    // Debounce saves to avoid too many API calls
    this.saveSubject.pipe(
      debounceTime(500),
      switchMap(() => {
        console.log('Auto-saving user settings...');
        if (!this.userSettings) {
          console.warn('No user settings to save');
          return of(void 0);
        }
        return this.platformPortalApiService.updateUserSettings(this.userSettings).pipe(
          tap(() => console.log('Settings saved successfully')),
          catchError(err => {
            console.error('Error saving settings:', err);
            return of(void 0);
          })
        );
      })
    ).subscribe();
  }

  getUserSettings(): Observable<UserSettings> {
    if (this.userSettings) {
      return of(this.userSettings);
    }

    return this.platformPortalApiService.getUserSettings().pipe(
      map(apiSettings => new UserSettings(apiSettings)),
      tap(settings => {
        this.userSettings = settings;
        this.settingsSubject.next(settings);

        // Subscribe to changes from the model
        settings.onChange.subscribe(() => {
          console.log('Settings changed, triggering update');
          this.settingsSubject.next(settings);
          this.saveSubject.next();
        });
      })
    );
  }

  updateUserSettings(settings: UserSettings): Observable<void> {
    this.userSettings = settings;
    this.settingsSubject.next(settings);
    return this.platformPortalApiService.updateUserSettings(settings);
  }

  // Force an immediate save regardless of debounce timer
  saveNow(): Observable<void> {
    if (!this.userSettings) {
      return this.getUserSettings().pipe(
        switchMap(() => this.platformPortalApiService.updateUserSettings(this.userSettings!))
      );
    }
    console.log('Saving settings now...');
    return this.platformPortalApiService.updateUserSettings(this.userSettings);
  }

  // Public helper to ensure settings are loaded
  public ensureSettings(): Observable<UserSettings> {
    return this.userSettings ? of(this.userSettings) : this.getUserSettings();
  }

  // Save changes after using CRUD methods
  saveChanges(): Observable<void> {
    return this.ensureSettings().pipe(
      switchMap(settings => this.updateUserSettings(settings))
    );
  }
}
